import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { CheckCircle2, Send, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { createLead } from '../api';

const ContactModal = ({ open, onOpenChange, title = 'Get in Touch', subtitle = "Tell us about your security needs and we'll be in touch within 24 hours.", source = 'contact' }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '', website: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createLead({ ...form, source });
      setSubmitted(true);
      toast({ title: 'Message sent', description: "We'll get back to you shortly." });
      setTimeout(() => {
        setSubmitted(false);
        setForm({ name: '', email: '', company: '', message: '', website: '' });
        onOpenChange(false);
      }, 1800);
    } catch (err) {
      toast({ title: 'Failed to send', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d1424] border border-cyan-400/20 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">{subtitle}</DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center py-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-cyan-400 mb-3" />
            <p className="text-lg font-semibold">Thanks, {form.name || 'friend'}!</p>
            <p className="text-slate-400 text-sm mt-1">Your message has been received.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            {/* Honeypot — invisible to humans; bots that auto-fill all fields will trip this */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
              <label>Website (leave blank)
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={e => setForm({ ...form, website: e.target.value })}
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs mb-1.5 block">Full Name</Label>
                <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1.5 block">Work Email</Label>
                <Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" placeholder="jane@company.com" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-1.5 block">Company</Label>
              <Input required value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" placeholder="Acme Corp" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-1.5 block">How can we help?</Label>
              <Textarea required rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" placeholder="Tell us about your compliance or security needs..." />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-70 text-slate-900 font-semibold rounded-md h-11 shadow-[0_0_24px_rgba(34,211,238,0.35)]">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>) : (<>Send Message <Send className="w-4 h-4 ml-1" /></>)}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
